import { startOfWeek, endOfWeek, getWeek, setWeek, setDay, addDays, format } from 'date-fns';
import { asBrowserDate } from '@/utilities/convertToISO';

/**
 * Thin wrappers around date-fns that bake in the tenant's week-start day.
 * Callers pass `weekStartsOn` resolved via `useWeekStartsOn()` (in components)
 * or via the org-week-starts-on map (in cron / batch contexts).
 *
 * 0 = Sunday, 1 = Monday.
 */

/**
 * Guard against the Invalid Date a MUI DatePicker can emit while a value is being typed
 * (date-fns `format` throws on invalid dates, so callers must filter these out first).
 */
export const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

export const startOfPayPeriod = (date, weekStartsOn) => startOfWeek(date, { weekStartsOn });

export const endOfPayPeriod = (date, weekStartsOn) => endOfWeek(date, { weekStartsOn });

export const payPeriodWeekNumber = (date, weekStartsOn) => getWeek(date, { weekStartsOn });

export const setPayPeriodWeek = (date, weekNumber, weekStartsOn) => setWeek(date, weekNumber, { weekStartsOn });

/**
 * Build the labeled pay-period dropdown options: current week + N prior weeks.
 * Returns options in newest-first order.
 *
 * Each option:
 *   { label: 'Feb 15 – Feb 21', value: { sow: Date, eow: Date } }
 */
export const buildPayPeriodOptions = (today, weekStartsOn, count = 11) => {
    const options = [];
    for (let i = 0; i < count; i++) {
        const target = addDays(today, -7 * i);
        const sow = startOfPayPeriod(target, weekStartsOn);
        const eow = endOfPayPeriod(target, weekStartsOn);
        options.push({
            label: `${format(sow, 'MMM d')} – ${format(eow, 'MMM d')}`,
            value: { sow, eow },
        });
    }
    return options;
};

/**
 * Format a stored invoice's pay period for display, e.g. '09/20/2026 - 09/26/2026'.
 * pay_period_start / pay_period_end are calendar dates stored as midnight UTC with an inclusive end,
 * so they are read as those dates in any browser zone. '' when either is missing.
 */
export const formatPayPeriod = (payPeriodStart, payPeriodEnd) => {
    if (!payPeriodStart || !payPeriodEnd) return '';
    const asDate = (value) => format(asBrowserDate(value), 'MM/dd/yyyy');
    return `${asDate(payPeriodStart)} - ${asDate(payPeriodEnd)}`;
};

/**
 * The edits deadline for a stored invoice: Thursday of the calendar week holding the day after
 * pay_period_end. That day is a Sunday or Monday for Sun-Sat and Mon-Sun cadences, so the Thursday is
 * always the one following the period. Local midnight of that date; null without a pay_period_end.
 */
export const editsDueDate = (payPeriodEnd) =>
    payPeriodEnd ? setDay(addDays(asBrowserDate(payPeriodEnd), 1), 4, { weekStartsOn: 0 }) : null;

/**
 * Build the pay-period dropdown weeks for the accounting screens (Payables / Receivables).
 * Newest-first: index 0 is the current week. Each option carries a stable `key`, which the
 * shared PayPeriodPicker uses to resolve the dropdown selection
 * (`weeks.find(({ key }) => key === value)`). Anchored to the org's `weekStartsOn` so the
 * boundaries line up with how `pay_period_start` is computed when invoices are created.
 *
 *   { key, sow: Date, eow: Date, week_number, label }
 */
export const buildPayPeriodWeeks = (today, weekStartsOn, count = 5) => {
    return Array.from({ length: count }, (_, idx) => {
        const day = addDays(today, -7 * idx);
        const sow = startOfPayPeriod(day, weekStartsOn);
        const eow = endOfPayPeriod(day, weekStartsOn);
        const range = `${format(sow, 'M/d/yyyy')} - ${format(eow, 'M/d/yyyy')}`;
        return {
            key: `week-${idx}`,
            sow,
            eow,
            week_number: payPeriodWeekNumber(day, weekStartsOn),
            label: idx === 0 ? `Current Week (${range})` : range,
        };
    });
};

/**
 * Hasura `where` conditions filtering carrier_invoices to a pay-period window.
 *
 * `pay_period_start` is a calendar date stored as midnight UTC, so the bounds are the
 * selected week's calendar dates as 'YYYY-MM-DD' strings, never local-midnight instants.
 * The upper bound is exclusive of the day after the week (`_lt`), which also keeps older
 * rows stored at a later time of day on their week.
 * Returns [] when no week is selected so callers can spread it unconditionally.
 */
export const payPeriodWhere = (week) => {
    if (!week?.sow || !week?.eow) return [];
    return [
        { pay_period_start: { _gte: format(week.sow, 'yyyy-MM-dd') } },
        { pay_period_start: { _lt: format(addDays(week.eow, 1), 'yyyy-MM-dd') } },
    ];
};

/** Sentinel key marking a `filter.week` produced by the picker's "Custom Range" option. */
export const CUSTOM_RANGE_KEY = 'custom';

const customRangeLabelFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

/**
 * Build a `filter.week` object for an ad-hoc custom date range chosen in the picker.
 * Normalizes to inclusive local-day boundaries (start-of-day .. end-of-day) so the
 * range matches the user's intent across the Payables, Receivables, and Invoices
 * screens. Returns null when either endpoint is missing.
 */
export const buildCustomRangeWeek = (sow, eow) => {
    if (!sow || !eow) return null;
    return {
        key: CUSTOM_RANGE_KEY,
        sow: new Date(new Date(sow).setHours(0, 0, 0, 0)),
        eow: new Date(new Date(eow).setHours(23, 59, 59, 999)),
        label: `${customRangeLabelFormat.format(new Date(sow))} - ${customRangeLabelFormat.format(new Date(eow))}`,
        isCustom: true,
    };
};
